'use client'

import { Button } from '@/core/components/form/button/button'
import { LuShieldCheck, LuShieldOff } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import { useShowAdult } from '@/features/veluvit/hooks/use-show-adult'
import { openAdultVerify } from '@/features/veluvit/services/preset.actions'
import { saveShowAdult } from '@/features/veluvit/services/profile.actions'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

/**
 * 세이프 토글 — 머리줄 오른쪽 영역의 맨 왼쪽(모바일 머리줄 · PC 상단 막대).
 * 스위치 모양 대신 방패 알약 하나: 세이프(기본)면 초록 방패 + '세이프', 끄면 코랄 테두리 + 방패 끔 + '언세이프'.
 * 값은 계정 설정(user.settings.showAdult)에 저장한다 — 다른 기기에서 로그인해도 같다(기기별 아님, 2026-10-07).
 * 언세이프를 켤 때는 서버 성인 인증(config.adult.eligible)이 있어야 한다 — 없으면 성인 인증 창을 먼저 띄우고 통과하면 켠다.
 * 끄는 쪽(세이프로 돌아가기)은 언제든 바로 된다. 홈 목록은 세이프일 때 19+ 작품을 빼고 보여 준다(feed-home).
 */
/** 세이프 모드 값 + 바꾸기 — 머리줄 토글 · 마이 설정 줄이 같이 쓴다(언세이프 켜기는 성인 인증 먼저) */
export function useSafeMode() {
  const showAdult = useShowAdult()
  const eligible = useSessionStore((s) => !!s.data?.config?.adult?.eligible)
  const toggle = () => {
    const session = useSessionStore.getState().data
    const verified = !!session?.config.adult?.eligible
    if (session?.user?.settings?.showAdult && verified) {
      saveShowAdult(false, '세이프예요. 19+ 작품을 가렸어요')
      return
    }
    const turnOn = () =>
      saveShowAdult(true, '언세이프예요. 19+ 작품도 함께 보여요')
    if (verified) turnOn()
    else openAdultVerify(turnOn)
  }
  return { showAdult, eligible, toggle }
}

export default function SafeToggle({ className }: { className?: string }) {
  const { showAdult, toggle } = useSafeMode()

  return (
    <Button
      type="button"
      role="switch"
      aria-checked={!showAdult}
      aria-label={
        showAdult ? '언세이프 — 19+ 작품 보는 중' : '세이프 — 19+ 작품 가림'
      }
      title={
        showAdult
          ? '언세이프예요. 누르면 세이프로 돌아가요'
          : '세이프예요. 누르면 언세이프로 바뀌어요(성인 인증 필요)'
      }
      onClick={toggle}
      className={cn(
        'inline-flex h-8 flex-none cursor-pointer items-center gap-1 rounded-full px-2.5 text-c13 leading-none font-bold whitespace-nowrap transition-colors duration-200',
        showAdult
          ? 'text-point ring-[1.5px] ring-point/55 ring-inset hover:bg-point/10'
          : 'bg-foreground/[0.07] text-t2 hover:bg-foreground/[0.12] hover:text-t1',
        className
      )}
      variant="bare"
    >
      {showAdult ? (
        <LuShieldOff aria-hidden="true" className="size-4" />
      ) : (
        <LuShieldCheck aria-hidden="true" className="size-4 text-ok" />
      )}
      {showAdult ? '언세이프' : '세이프'}
    </Button>
  )
}
